import { useLocation, useNavigate } from "react-router-dom";
import { Stack, styled, Typography } from "@mui/material";
import { TopPage } from "@/components/elements/TopPage";
import { InnerBox } from "@/components/elements/InnerBox";
import { CompanyAfterImport } from "@/features/company/components/company-list/CompanyAfterImport";
import { Button } from "@/components/elements/Button";
import { AppRoutes } from "@/routes/config";
import React, { FC, useEffect, useState } from "react";
import { importCompanyApi, useCompanyList } from "@/api/services/main/company";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useDialog } from "@/hooks/useDialog";
import { black, divider, orange, state } from "@/theme/colors";
import { IconSave } from "@/assets/icons";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { rounded } from "@/theme/variables";
import { usePagination } from "@/hooks/usePagination";
import { TCompany } from "@/features/talent/types/talentForm";
import { sanitizeInput } from "@/utils/pieces";

type RowError = {
  row: number;
  messages: string[];
};

export const CompanyImportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = location.state || {};
  const totalRow = company?.length ?? 0;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<RowError[]>([]);

  const confirmModal = useDialog();
  const backModal = useDialog();
  const page = usePagination();
  const { companyOptions, isLoading } = useCompanyList({
    isAll: "1",
    ...page,
  });

  const listCompanyName = companyOptions.map((company) => company.label);
  const checkImport = (companies: TCompany[], existingCompanyNames: string[]) => {
    const errorsByRow: RowError[] = [];

    const nameToIndices: Record<string, number[]> = {};
    companies.forEach((item, idx) => {
      const name = item.companyName ?? "";
      if (!nameToIndices[name]) {
        nameToIndices[name] = [];
      }
      nameToIndices[name].push(idx);
    });

    companies.forEach((item, idx) => {
      const rowNumber = idx + 1;
      const rowErrors: string[] = [];
      const name = sanitizeInput(item.companyName).replace(/\s+/g, "") ?? "";

      const indices = nameToIndices[name];

      if (existingCompanyNames.includes(name)) {
        rowErrors.push(`${rowNumber}行目の必須項目「企業名」（${name}）が既に登録されています。`);
      }

      if (indices && indices.length > 1) {
        const rows = indices.map((i) => `${i + 1}行目`).join("、");
        rowErrors.push(`${rows}の項目「企業名」（${name}）が重複しています。`);
      }

      if (rowErrors.length > 0) {
        errorsByRow.push({ row: rowNumber, messages: rowErrors });
      }
    });

    const listError = errorsByRow.map((item) => ({
      row: item.row,
      messages: Array.from(new Set(item.messages)),
    }));

    const seen = new Set<string>();

    return listError.map((item) => {
      const uniqueList: string[] = [];

      for (const msg of item.messages) {
        if (!seen.has(msg)) {
          seen.add(msg);
          uniqueList.push(msg);
        }
      }

      return {
        row: item.row,
        messages: uniqueList,
      };
    });
  };

  useEffect(() => {
    if (!isLoading) {
      const newErrors = checkImport(company, listCompanyName);
      setErrors(newErrors);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!company) {
      navigate(AppRoutes.companyList);
    }
  }, [company]);

  const handleSaveData = async () => {
    if (!company) return;
    setIsSubmitting(true);
    try {
      await importCompanyApi(company);
      toast.success(API_SUCCESS_MESSAGE);
      navigate(AppRoutes.companyList);
    } catch (error) {
      toast.error(API_UNKNOWN_ERROR_MESSAGE);
    }
    setIsSubmitting(false);
  };

  const TitleInnerBox: FC = () => {
    return (
      <Typography variant="sub16Semi" color={black[70]}>
        一括インポート：
        <Typography variant="head20Semi" color={black[30]}>
          {totalRow}
        </Typography>{" "}
        件
      </Typography>
    );
  };

  return (
    <>
      <PageWrapper>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <TopPage title="企業インポート" enablePageBack={false} />
        </Stack>

        <Content>
          <InnerBox borderColor={divider.high} padding={3} mt={2} showDivider={false} title={<TitleInnerBox />}>
            <Stack
              flex={1}
              gap={3}
              sx={{
                "& .MuiTableContainer-root": {
                  paddingBottom: "10px",
                },
              }}
            >
              <CompanyAfterImport rows={company ?? []} isLoading={false} />
            </Stack>
            {errors.length > 0 && (
              <Stack
                sx={{ mt: "32px", p: "8px", background: state.incomplete_secondary, color: orange[80], maxHeight: "160px", overflowY: "auto" }}
                borderRadius={rounded.xs}
              >
                {errors.map((err, index) => (
                  <Typography key={index} component="p" variant="cap12">
                    {err.messages.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </Typography>
                ))}
                <Typography component="p" variant="cap12">
                  修正が必要な場合は修正後に再インポートしてください。
                </Typography>
              </Stack>
            )}
          </InnerBox>
        </Content>

        <FooterContainer>
          <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="ghost" size="sm" onClick={() => backModal.show()}>
              戻る
            </Button>
            <Stack direction="row" gap={3}>
              <Button variant="primary" size="sm" disabled={isSubmitting} onClick={handleSaveData} startIcon={<IconSave />}>
                保存
              </Button>
            </Stack>
          </Stack>
        </FooterContainer>
      </PageWrapper>
      <ConfirmSaveModal open={backModal.open} onOk={() => navigate(AppRoutes.companyList)} onClose={backModal.cancel}>
        <Typography component="p" variant="subtitle1">
          インポートが終了しておりませんが戻ってもよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </>
  );
};

const PageWrapper = styled(Stack)`
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;

const Content = styled(Stack)`
  flex: 1;
`;
